<script lang="ts">
  import { onMount } from 'svelte';

  import { i18n } from '#lib/i18n.js';
  import Select from '#lib/ui/primitives/Select.svelte';

  interface Props {
    onScan: (data: Uint8Array) => void;
  }

  let { onScan }: Props = $props();

  const SCAN_INTERVAL_MS = 200;
  const MAX_FRAME_EDGE = 720;
  const HEADER = [0x4d, 0x41, 0x54, 0x52, 0x49, 0x58];

  let video = $state<HTMLVideoElement>();
  let failed = $state(false);
  let cameras = $state<MediaDeviceInfo[]>([]);
  let selectedCamera = $state('');
  let switching = $state(false);
  let switchCamera = $state<(deviceId: string) => void>(() => {});

  function isVerificationCode(data: number[]): boolean {
    return HEADER.every((byte, index) => data[index] === byte);
  }

  onMount(() => {
    let stream: MediaStream | undefined;
    let timer: ReturnType<typeof setInterval> | undefined;
    let stopped = false;
    let generation = 0;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });

    function stopStream(): void {
      clearInterval(timer);
      stream?.getTracks().forEach((track) => track.stop());
      stream = undefined;
      if (video) video.srcObject = null;
    }

    async function start(deviceId?: string): Promise<void> {
      const current = ++generation;
      switching = true;
      failed = false;
      stopStream();
      let next: MediaStream;
      try {
        next = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: deviceId ? { deviceId: { exact: deviceId } } : { facingMode: 'environment' },
        });
      } catch {
        if (!stopped && current === generation) {
          failed = true;
          switching = false;
        }
        return;
      }
      if (stopped || current !== generation || !video || !context) {
        next.getTracks().forEach((track) => track.stop());
        return;
      }
      stream = next;
      selectedCamera = next.getVideoTracks()[0]?.getSettings().deviceId ?? deviceId ?? '';
      // Camera labels and additional devices may only be available after permission is granted.
      void navigator.mediaDevices.enumerateDevices?.().then(
        (devices) => {
          if (!stopped && current === generation) {
            cameras = devices.filter((device) => device.kind === 'videoinput' && device.deviceId);
          }
        },
        () => {}
      );
      const { default: jsQR } = await import('jsqr');
      if (stopped || current !== generation) return;
      const element = video;
      element.srcObject = next;
      await element.play().catch((error: unknown) => {
        console.debug('[sable settings] camera preview did not start', error);
      });
      if (stopped || current !== generation) return;
      switching = false;

      timer = setInterval(() => {
        if (!element.videoWidth) return;
        const scale = Math.min(
          1,
          MAX_FRAME_EDGE / Math.max(element.videoWidth, element.videoHeight)
        );
        canvas.width = Math.round(element.videoWidth * scale);
        canvas.height = Math.round(element.videoHeight * scale);
        context.drawImage(element, 0, 0, canvas.width, canvas.height);
        const frame = context.getImageData(0, 0, canvas.width, canvas.height);
        const found = jsQR(frame.data, frame.width, frame.height, {
          inversionAttempts: 'dontInvert',
        });
        if (!found || !isVerificationCode(found.binaryData)) return;
        stopped = true;
        stopStream();
        onScan(new Uint8Array(found.binaryData));
      }, SCAN_INTERVAL_MS);
    }

    switchCamera = (deviceId) => void start(deviceId);
    void start();

    return () => {
      stopped = true;
      generation += 1;
      stopStream();
    };
  });
</script>

{#if cameras.length > 1}
  <Select
    items={cameras.map((camera, index) => ({
      value: camera.deviceId,
      label: camera.label || `${i18n.t('settings.callCameraDevice')} ${String(index + 1)}`,
      labelClass: 'literal-label',
    }))}
    value={selectedCamera}
    disabled={switching}
    aria-label={i18n.t('settings.callCameraDevice')}
    onValueChange={switchCamera}
  />
{/if}
<div class="verification-scanner" hidden={failed}>
  <video bind:this={video} muted playsinline aria-label={i18n.t('settings.scanningQr')}></video>
</div>
{#if failed}
  <p class="verification-scanner-failed" role="alert">{i18n.t('settings.cameraUnavailable')}</p>
{:else}
  <p class="verification-scanner-hint" role="status">{i18n.t('settings.scanningQr')}</p>
{/if}

<style>
  .verification-scanner {
    aspect-ratio: 1;
    background: var(--media-scrim-solid);
    border-radius: var(--radii-400);
    overflow: hidden;
    width: min(16rem, 100%);
  }

  .verification-scanner video {
    display: block;
    height: 100%;
    object-fit: cover;
    width: 100%;
  }

  .verification-scanner-hint,
  .verification-scanner-failed {
    color: var(--surface-var-on-container);
    font-size: var(--font-size-small);
    margin: 0;
  }
</style>
